/**
 * DeleteInspectionModal Component
 * Modal for confirming deletion of an inspection record
 */

import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Checkbox,
	FormControlLabel,
	IconButton,
} from '@mui/material';
import { Close, WarningAmber } from '@mui/icons-material';
import './index.scss';

const DeleteInspectionModal = ({
	open,
	onClose,
	inspection,
	onConfirmDelete,
}) => {
	const { messages } = useIntl();
	const [certified, setCertified] = useState(false);

	const handleCertifiedChange = (event) => {
		setCertified(event.target.checked);
	};

	const handleDelete = () => {
		if (onConfirmDelete && certified) {
			onConfirmDelete(inspection?.id, certified);
		}
	};

	const handleClose = () => {
		setCertified(false);
		onClose();
	};

	// Build inspection details for display
	const getInspectionDetails = () => {
		if (!inspection) return {};

		const startDateTime = inspection.startDate && inspection.startTime
			? `${inspection.startDate} ${inspection.startTime}`
			: '';

		return {
			status: inspection.failed === true ? 'Failed' : 'Passed',
			dateTime: startDateTime,
			driverTag: inspection.inspector || 'Unknown',
			assetNumber: inspection.assetViewModel?.firstAssetName || 'Unknown',
			assetTag: inspection.assetViewModel?.firstAssetId || 'Unknown',
			division: inspection.homeLocation || 'Unknown',
		};
	};

	const details = getInspectionDetails();

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			className="delete-inspection-modal"
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle className="modal-title">
				<div className="title-content">
					<WarningAmber className="title-icon" />
					<span>{messages['evirPx_delete_inspection_record'] || 'Delete Inspection Record?'}</span>
				</div>
				<IconButton onClick={handleClose} className="close-button" size="small">
					<Close />
				</IconButton>
			</DialogTitle>

			<DialogContent className="modal-content">
				<p className="modal-subtitle">
					{messages['evirPx_action_cannot_be_undone'] || 'This action cannot be undone'}
				</p>

				<div className="compliance-warning">
					<WarningAmber className="warning-icon" />
					<div className="warning-content">
						<span className="warning-title">
							{messages['evirPx_fmcsa_compliance_warning'] || 'FMCSA Compliance Warning:'}
						</span>
						<span className="warning-text">
							{messages['evirPx_delete_compliance_warning'] || 'Deleting inspections may remove FMCSA-required data. This could impact your compliance record and audit trail.'}
						</span>
					</div>
				</div>

				<div className="inspection-details">
					<h4 className="details-title">
						{messages['evirPx_inspection_to_delete'] || 'Inspection to be deleted:'}
					</h4>
					<div className="details-grid">
						<div className="detail-row">
							<span className="detail-label">{messages['evirPx_status'] || 'Status'}:</span>
							<span className={`detail-value status-${details.status?.toLowerCase()}`}>
								{details.status}
							</span>
						</div>
						<div className="detail-row">
							<span className="detail-label">{messages['evirPx_date_time'] || 'Date/Time'}:</span>
							<span className="detail-value">{details.dateTime}</span>
						</div>
						<div className="detail-row">
							<span className="detail-label">{messages['evirPx_driver_tag'] || 'Driver Tag'}:</span>
							<span className="detail-value">{details.driverTag}</span>
						</div>
						<div className="detail-row">
							<span className="detail-label">{messages['evirPx_asset_number'] || 'Asset Number'}:</span>
							<span className="detail-value">{details.assetNumber}</span>
						</div>
						<div className="detail-row">
							<span className="detail-label">{messages['evirPx_asset_tag'] || 'Asset Tag'}:</span>
							<span className="detail-value">{details.assetTag}</span>
						</div>
						<div className="detail-row">
							<span className="detail-label">{messages['evirPx_division'] || 'Division'}:</span>
							<span className="detail-value">{details.division}</span>
						</div>
					</div>
				</div>

				<div className="certification-checkbox">
					<FormControlLabel
						control={
							<Checkbox
								checked={certified}
								onChange={handleCertifiedChange}
								color="primary"
							/>
						}
						label={
							<span className="certification-label">
								{messages['evirPx_certify_delete'] || 'I certify this update is true and acknowledge that removing this inspection data may affect compliance records.'}
							</span>
						}
					/>
				</div>
			</DialogContent>

			<DialogActions className="modal-actions">
				<Button onClick={handleClose} className="cancel-button">
					{messages['evirPx_cancel'] || 'Cancel'}
				</Button>
				<Button
					onClick={handleDelete}
					variant="contained"
					className="delete-button"
					disabled={!certified}
				>
					{messages['evirPx_delete_inspection'] || 'Delete Inspection'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default DeleteInspectionModal;
